/* eslint-disable node/no-process-env, no-restricted-globals */
import createLogger from 'pino'
import stdSerializers from 'pino-std-serializers'

import { BaseError, ErrorCode, KnownError } from './error'

const serializers = {
  req: stdSerializers.req,
  res: stdSerializers.res,
  err: (error: KnownError) => {
    if (error instanceof BaseError) {
      return {
        name: error.constructor.name,
        message: error.message,
        stack: error.stack,
        ...error.extensions,
      }
    }

    const data = error.toJSON
      ? error.toJSON()
      : { message: error.message, stack: error.stack, code: ErrorCode.ERROR }

    return {
      name: error.constructor.name,
      ...data,
    }
  },
}

/**
 * A hook to process errors consistently
 *
 * @see https://github.com/pinojs/pino/blob/v6.14.0/docs/api.md
 */
const hooks = {
  logMethod(args: any[], method: any, level: number) {
    if (args[0] instanceof Error && level === levels.error) {
      const error = args.shift()
      return method.apply(this, [{ err: error }, ...args])
    }

    return method.apply(this, args)
  },
}

export const log = createLogger({
  serializers,
  hooks,
  prettyPrint:
    process.env['NODE_ENV'] !== 'production'
      ? { colorize: true, levelFirst: true }
      : false,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  prettifier:
    process.env['NODE_ENV'] !== 'production' && typeof require === 'function'
      ? require('pino-colada')
      : null,
  level: process.env['LOG_LEVEL'] ?? 'info',
  base: {
    ver: process.env['GAIA_VERSION'] ?? 'unknown',
    env: process.env['GAIA_ENV'] ?? 'unknown',
    stack: process.env['GAIA_STACK'] ?? 'unknown',
  },
})

// bind so that log methods can be passed more easily as functions without losing context
log.error = log.error.bind(log)
log.warn = log.warn.bind(log)
log.info = log.info.bind(log)
log.debug = log.debug.bind(log)
log.trace = log.trace.bind(log)

export const levels = {
  fatal: 60,
  error: 50,
  warn: 40,
  info: 30,
  debug: 20,
  trace: 10,
} as const

/**
 * Create bindings for unhandled error events we want to log
 */
export const captureUnhandled = () => {
  if (typeof process === 'undefined') {
    return
  }

  process.on('unhandledRejection', (err) => {
    log.error({ err, unhandled: true, async: true })
  })

  process.on('uncaughtException', (err) => {
    log.error({ err, unhandled: true })
  })
}
